import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Delete,
  StarBorder,
  Star,
  FileDownload,
  FileUpload,
} from '@mui/icons-material';
import { productAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import useDebounce, { usePersistedState } from '../../utils/useDebounce';
import {
  PageHeader,
  ConfirmDialog,
  BulkActionBar,
  emptyStateTextStyles,
  tableHeadRowStyles,
  tableBodyRowStyles,
  tablePaginationStyles,
  tableAvatarStyles,
  primaryLinkStyles,
  standardTextStyles,
} from '../../components/common';

const ProductList = () => {
  const navigate = useNavigate();
  const { canEditProducts, canDelete } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = usePersistedState('product_list_page', 0);
  const [rowsPerPage, setRowsPerPage] = usePersistedState('product_list_rpp', 80);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = usePersistedState('product_list_search', '');
  const debouncedSearch = useDebounce(search, 400);
  const [viewMode, setViewMode] = usePersistedState('product_list_view', 'kanban');
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Skip the initial mount so the restored `page` is not clobbered.
  const isFirstSearchChange = useRef(true);
  useEffect(() => {
    if (isFirstSearchChange.current) {
      isFirstSearchChange.current = false;
      return;
    }
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch,
      });
      setProducts(response.data.products);
      setTotal(response.data.pagination.totalItems);
    } catch (error) {
      toast.error('Failed to load products');
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (e, product) => {
    e.stopPropagation();
    try {
      await productAPI.update(product._id, { isFavorite: !product.isFavorite });
      setProducts(products.map(p => 
        p._id === product._id ? { ...p, isFavorite: !p.isFavorite } : p
      ));
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Handle row selection
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(products.map(p => p._id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  // Single delete
  const handleDeleteClick = (e, product) => {
    e.stopPropagation();
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    try {
      setDeleting(true);
      await productAPI.delete(productToDelete._id);
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete product');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  // Bulk delete
  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setDeleting(true);
      await productAPI.bulkDelete(selectedRows);
      toast.success(`${selectedRows.length} product(s) deleted successfully`);
      setSelectedRows([]);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete products');
    } finally {
      setDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      {/* Page Header */}
      <PageHeader
        title="Products"
        newButtonText="New"
        onNew={canEditProducts() ? () => navigate('/products/new') : undefined}
        showNewButton={canEditProducts()}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search products..."
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        viewModes={['kanban', 'list']}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onSettingsClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
      />

      {/* Content Area */}
      {viewMode === 'list' ? (
        /* List View - Table */
        <Box sx={{ backgroundColor: 'white', overflowX: 'auto' }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: { xs: 600, md: 'auto' } }}>
              <TableHead>
                <TableRow sx={tableHeadRowStyles}>
                  <TableCell padding="checkbox" sx={{ width: 40 }}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={selectedRows.length === products.length && products.length > 0}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 40 }}></TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Stock</TableCell>
                  <TableCell align="center" sx={{ width: 60 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography sx={emptyStateTextStyles}>
                        No products found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow
                      key={product._id}
                      hover
                      selected={selectedRows.includes(product._id)}
                      sx={tableBodyRowStyles}
                      onClick={() => navigate(`/products/${product._id}`)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(product._id)}
                          onChange={() => handleSelectRow(product._id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleToggleFavorite(e, product)}
                          sx={{ p: 0 }}
                        >
                          {product.isFavorite ? (
                            <Star sx={{ fontSize: 18, color: '#f5c518' }} />
                          ) : (
                            <StarBorder sx={{ fontSize: 18, color: '#999' }} />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {product.images && product.images.length > 0 && product.images[0].url ? (
                            <Avatar
                              src={product.images[0].url}
                              variant="rounded"
                              sx={{ width: 32, height: 32 }}
                            />
                          ) : (
                            <Avatar
                              variant="rounded"
                              sx={{ ...tableAvatarStyles, bgcolor: '#875A7B' }}
                            >
                              {product.name?.charAt(0)?.toUpperCase() || 'P'}
                            </Avatar>
                          )}
                          <Typography sx={primaryLinkStyles}>
                            {product.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={standardTextStyles}>
                          {product.category || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>
                          {formatCurrency(product.unitPrice)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{
                          fontSize: '13px',
                          color: (product.stockQuantity || 0) <= 0 ? '#dc3545' : '#333',
                          fontWeight: (product.stockQuantity || 0) <= 0 ? 500 : 400,
                        }}>
                          {product.stockQuantity || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        {canDelete() && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteClick(e, product)}
                            sx={{ color: '#adb5bd', '&:hover': { color: '#dc3545' } }}
                          >
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 80]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={tablePaginationStyles}
          />
        </Box>
      ) : (
        /* Kanban View - Cards */
        <Box sx={{ p: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : products.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography sx={emptyStateTextStyles}>
                No products found.
              </Typography>
              {canEditProducts() && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/products/new')}
                  sx={{
                    backgroundColor: '#875A7B',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#714B68' },
                  }}
                >
                  Add your first product
                </Button>
              )}
            </Box>
          ) : (
            <Grid container spacing={2}>
              {products.map((product) => (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={product._id}>
                  <Card
                    sx={{
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      boxShadow: 'none',
                      cursor: 'pointer',
                      transition: 'box-shadow 0.2s',
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    onClick={() => navigate(`/products/${product._id}`)}
                  >
                    <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleToggleFavorite(e, product)}
                          sx={{ p: 0, mr: 1 }}
                        >
                          {product.isFavorite ? (
                            <Star sx={{ fontSize: 18, color: '#f5c518' }} />
                          ) : (
                            <StarBorder sx={{ fontSize: 18, color: '#999' }} />
                          )}
                        </IconButton>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            fontWeight: 400,
                            color: '#333',
                            lineHeight: 1.4,
                            flex: 1,
                            wordBreak: 'break-word',
                          }}
                        >
                          "{product.name}"
                        </Typography>
                      </Box>
                      {product.images && product.images.length > 0 && product.images[0].url && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 'auto',
                            mb: 1,
                          }}
                        >
                          <img
                            src={product.images[0].url}
                            alt={product.name}
                            style={{
                              maxWidth: 60,
                              maxHeight: 60,
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                      )}
                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: '#666',
                          mt: 'auto',
                        }}
                      >
                        Price: ₹ {(product.unitPrice || 0).toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Settings Menu - Import/Export */}
      <Menu
        anchorEl={settingsMenuAnchor}
        open={Boolean(settingsMenuAnchor)}
        onClose={() => setSettingsMenuAnchor(null)}
        PaperProps={{
          sx: {
            minWidth: 180,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            mt: 0.5,
          }
        }}
      >
        <MenuItem onClick={() => { toast.info('Export All feature coming soon'); setSettingsMenuAnchor(null); }}>
          <FileDownload sx={{ fontSize: 18, color: '#666', mr: 1.5 }} />
          Export All (CSV)
        </MenuItem>
        <MenuItem onClick={() => { toast.info('Import records feature coming soon'); setSettingsMenuAnchor(null); }}>
          <FileUpload sx={{ fontSize: 18, color: '#666', mr: 1.5 }} />
          Import from CSV
        </MenuItem>
      </Menu>

      {/* Bulk Actions Bar */}
      {canDelete() && (
        <BulkActionBar
          selectedCount={selectedRows.length}
          onClearSelection={() => setSelectedRows([])}
          onDelete={handleBulkDelete}
        />
      )}

      {/* Single Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setProductToDelete(null); }}
        onConfirm={confirmDelete}
        title="Delete Product"
        message={`Are you sure you want to delete "${productToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
        loading={deleting}
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={confirmBulkDelete}
        title="Delete Products"
        message={`Are you sure you want to delete ${selectedRows.length} product(s)? This action cannot be undone.`}
        confirmText={`Delete ${selectedRows.length} Product(s)`}
        type="danger"
        loading={deleting}
      />
    </Box>
  );
};

export default ProductList;
